/**
 * What the Study Anything reader knows about where the student is, for the
 * tutor. A rendered Markdown book has no section ids: its headings are the
 * landmarks. "Where they are" is the heading above the middle of the screen; a
 * selected passage carries the heading it sits under; an answer's source
 * (`{kind: "lines", title}`) scrolls to the heading of that title.
 */

import { describeRange } from "../Tutor/anchors";

const HEADINGS = "h1, h2, h3, h4, h5, h6";
const BLOCKS = "p, li, pre, blockquote, td, th, h1, h2, h3, h4, h5, h6";

const textOf = (el) => (el?.textContent || "").replace(/\s+/g, " ").trim();

/** The headings of the book, in order, each given an id if it has none: [{id, text, level, el}]. */
export function headingsIn(root) {
  if (!root) return [];
  return [...root.querySelectorAll(HEADINGS)].map((el, i) => {
    if (!el.id) el.id = `heading-${i}`;
    return { id: el.id, text: textOf(el), level: Number(el.tagName[1]) || 1, el };
  });
}

/** The heading `el` sits under: the last heading before it in the book, or "". */
export function headingOf(root, el) {
  if (!root || !el) return "";
  let best = "";
  for (const h of root.querySelectorAll(HEADINGS)) {
    if (h === el || h.contains(el)) return textOf(h);
    // DOCUMENT_POSITION_FOLLOWING: `el` comes after `h`.
    if (h.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) best = textOf(h);
    else break;
  }
  return best;
}

/** Where they are: { section } — the heading above the middle of the pane (the one being read). */
export function placeIn(root, pane) {
  if (!root) return { section: "" };
  const view = (pane || document.documentElement).getBoundingClientRect();
  const top = pane ? view.top : 0;
  const bottom = pane ? view.bottom : window.innerHeight;
  const middle = (top + bottom) / 2;
  let section = "";
  for (const h of root.querySelectorAll(HEADINGS)) {
    const r = h.getBoundingClientRect();
    if (r.top <= middle) section = textOf(h);
    else break;
  }
  return { section: section.slice(0, 200) };
}

/**
 * The selection inside `root` as { text, section, prefix, suffix, around }, or
 * null. `section` is the heading the passage sits under; the words around it
 * come from its block (../Tutor/anchors), so a tab branched from it can find it again.
 */
export function selectionIn(root) {
  const sel = window.getSelection?.();
  if (!root || !sel || sel.isCollapsed || !sel.rangeCount) return null;
  const range = sel.getRangeAt(0);
  const node = range.commonAncestorContainer;
  const el = node.nodeType === 1 ? node : node.parentElement;
  if (!el || !root.contains(el)) return null;
  const text = sel.toString().replace(/\s+\n/g, "\n").trim();
  if (text.length < 3) return null;
  const startEl = range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentElement;
  const scope = startEl?.closest?.(BLOCKS) || el;
  const anchor = (scope && scope.contains(range.endContainer) ? describeRange(range, scope) : null) || {};
  return { text: text.slice(0, 4000), section: headingOf(root, startEl || el).slice(0, 200), chapter: null,
    prefix: anchor.prefix || "", suffix: anchor.suffix || "", around: anchor.around || "" };
}

/** Scroll the book to the heading an answer's source names and flash it. False if there is none. */
export function revealSource(root, source) {
  if (!root || !source) return false;
  const wanted = (source.title || source.section || "").replace(/\s+/g, " ").trim();
  if (!wanted) return false;
  const target = [...root.querySelectorAll(HEADINGS)].find((h) => textOf(h) === wanted)
    || [...root.querySelectorAll(HEADINGS)].find((h) => textOf(h).toLowerCase() === wanted.toLowerCase());
  if (!target) return false;
  target.scrollIntoView({ behavior: "smooth", block: "start" });
  if (target.animate) {
    target.animate([{ backgroundColor: "rgba(1,114,166,0.16)" }, { backgroundColor: "transparent" }],
      { duration: 1800, easing: "ease-out" });
  }
  return true;
}
